import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation, useMatches } from 'react-router';
import { useAuth } from '@/auth/AuthProvider';
import { useTenant } from '@/tenant/TenantProvider';
import { FullPageLoader, FullPageError } from './FullPage';
import { NotFoundPage } from './SystemPages';

/**
 * Route guards (layout routes). Order in routes.tsx:
 *   RequireAuth → RequireTenant → RequireActiveSubscription → portal pages
 *   RequireAuth → RequirePlatformAdmin → admin pages
 * The server is authoritative; guards only shape navigation.
 */

export function RequireAuth({ children }: { children?: ReactNode }) {
  const { status } = useAuth();
  const loc = useLocation();
  // An unknown URL lands on a catch-all route (handle.notFound). Signed out, that is a 404 — not a
  // detour through /login that ends on the same 404 after signing in.
  const unknownRoute = useMatches().some(
    (m) => (m.handle as { notFound?: boolean } | undefined)?.notFound,
  );
  if (status === 'loading') return <FullPageLoader />;
  if (status === 'signedOut') {
    if (unknownRoute) return <NotFoundPage />;
    const next = loc.pathname + loc.search;
    return (
      <Navigate
        to={next && next !== '/' ? `/login?next=${encodeURIComponent(next)}` : '/login'}
        replace
      />
    );
  }
  return children ?? <Outlet />;
}

/** Signed-in users who hit /login etc. go to `next` (or home). */
export function RedirectIfAuthed({ children }: { children?: ReactNode }) {
  const { status } = useAuth();
  const loc = useLocation();
  if (status === 'loading') return <FullPageLoader />;
  if (status === 'signedIn') {
    const next = new URLSearchParams(loc.search).get('next');
    return (
      <Navigate to={next && next.startsWith('/') && !next.startsWith('//') ? next : '/'} replace />
    );
  }
  return children ?? <Outlet />;
}

/** Requires a selected tenant; users with no business go to /setup. */
export function RequireTenant({ children }: { children?: ReactNode }) {
  const t = useTenant();
  if (t.status === 'loading' || t.status === 'idle') return <FullPageLoader />;
  if (t.status === 'error')
    return <FullPageError error={t.error} onRetry={() => void t.refresh()} />;
  // Platform admins without a business of their own land in the admin area.
  if (t.status === 'noTenant')
    return <Navigate to={t.isPlatformAdmin ? '/platform-admin' : '/setup'} replace />;
  return children ?? <Outlet />;
}

/**
 * Requires an active (or past_due grace) subscription; otherwise → /setup/plan. Canceled tenants
 * keep read-only access to the portal (billing policy: data stays visible; spending is blocked
 * server-side with 402 subscription_inactive).
 */
export function RequireActiveSubscription({ children }: { children?: ReactNode }) {
  const t = useTenant();
  if (!t.current) return <FullPageLoader />;
  if (!t.subscriptionActive && t.current.tenant.status !== 'canceled')
    return <Navigate to="/setup/plan" replace />;
  return children ?? <Outlet />;
}

/** Platform admin only (server re-checks `platform_admins` + custom claim). */
export function RequirePlatformAdmin({ children }: { children?: ReactNode }) {
  const t = useTenant();
  if (t.status === 'idle' || (t.status === 'loading' && !t.me)) return <FullPageLoader />;
  if (!t.isPlatformAdmin) return <Navigate to="/" replace />;
  return children ?? <Outlet />;
}
